import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  Button,
  Typography,
} from 'antd';
import InfiniteScroll from 'react-infinite-scroller';

import {
  setShoutoutModalState,
} from '../../../../context/tqConnectUserReducer';
import {
  useTQConnectProfileShoutouts,
} from '../../../../hooks/queries/tqConnectQueries/user';
import { numFormatterInUnit } from '../../../../shared/formatter';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { ROUTES } from '../../../../types/routes';
import { IUserShoutoutsFilters } from '../../../../types/tqConnectTypes/user';
import { UserAccountType } from '../../../../types/userTypes';
import { IUserProfileDetails } from '../../types';
import UserTabItemTypeSelector from '../UserTabItemTypeSelector';
import NoShoutouts from './NoShoutouts';
import ShoutoutCard from './ShoutoutCard';
import ShoutoutCardSkeleton from './ShoutoutCardSkeleton';

interface Props {
  userProfileId: string;
  userProfileDetails: IUserProfileDetails;
  userName: string;
  viewerType: UserAccountType;
  filters: IUserShoutoutsFilters;
  onChangeFilters: (
    key: keyof IUserShoutoutsFilters,
    value: IUserShoutoutsFilters[keyof IUserShoutoutsFilters],
  ) => void;
  isViewerSelf?: boolean;
  shoutoutReceived: number;
  shoutoutGiven: number;
  refreshTQProfileInfo: () => void;
}

const UserShoutouts: React.FC<Props> = ({
  userProfileId,
  userProfileDetails,
  userName,
  viewerType,
  filters,
  onChangeFilters,
  isViewerSelf,
  shoutoutReceived,
  shoutoutGiven,
  refreshTQProfileInfo,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const {
    id: currentUserId,
    name: currentUserName,
    profilePic: currentUserProfilePicUrl,
  } = useAppSelector((state) => state.user);
  const {
    userCommunityAccess: { canPostInChannels },
  } = useAppSelector((state) => state.community);

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const {
    data: userShoutoutsResult,
    isLoading: loading,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetchingNextPage,
  } = useTQConnectProfileShoutouts({
    queryForUser: userProfileId,
    type: filters.type,
  });

  const { list, total } = useMemo(() => {
    return {
      list: userShoutoutsResult?.data ?? [],
      total: userShoutoutsResult?.total ?? 0,
    };
  }, [userShoutoutsResult?.data, userShoutoutsResult?.total]);

  const loadMore = () => {
    if (hasMore && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    // const element = document.getElementById(USER_COMMUNITY_POSTS_SCROLL);
    const element = document.getElementsByClassName(
      'ant-tabs-content-holder',
    )[0] as HTMLElement;

    if (element) {
      scrollContainerRef.current = element;
    }
  }, [userProfileId]);

  const renderSkeletons = (length: number) => {
    return (
      <>
        {Array.from({ length }).map((_, index) => (
          <ShoutoutCardSkeleton key={`shoutout-card-skeleton-${index}`} />
        ))}
      </>
    );
  };

  return (
    <div className="tab__shoutouts">
      <div className="tab__shoutouts__header">
        <Typography.Title level={4} className="header__title">
          {viewerType === 'creator' || isViewerSelf
            ? 'Shoutouts'
            : `Shoutouts received ${shoutoutReceived ? `(${numFormatterInUnit(shoutoutReceived)})` : ''}`}
        </Typography.Title>

        {viewerType === 'creator' || isViewerSelf ? (
          <UserTabItemTypeSelector
            item="Shoutouts"
            selectedType={filters.type}
            onSelectType={(type) => onChangeFilters('type', type)}
            receivedCount={shoutoutReceived}
            givenCount={shoutoutGiven}
          />
        ) : (
          <Button
            className="siteBtn darkBtn"
            onClick={() => {
              dispatch(
                setShoutoutModalState({
                  isOpen: true,
                  givenBy: {
                    _id: currentUserId || '',
                    name: currentUserName || '',
                    profilePicUrl: currentUserProfilePicUrl || '',
                  },
                  givenTo: {
                    _id: userProfileId,
                    name: userName,
                    profilePicUrl: userProfileDetails.userProfile.profilePicUrl,
                  },
                  shoutout: {
                    to: {
                      _id: userProfileId,
                      name: userName,
                      profilePicUrl:
                        userProfileDetails.userProfile.profilePicUrl,
                    },
                  },
                  callback: refreshTQProfileInfo,
                }),
              );
              navigate(ROUTES.TQ_CONNECT_SHOUTOUT_CREATE, {}, { reset: true });
            }}>
            Give shoutout
          </Button>
        )}
      </div>

      <div className="shoutouts__list">
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={hasMore}
          useWindow={false}
          getScrollParent={() => scrollContainerRef.current}
          loader={
            (!loading && (hasMore || isFetchingNextPage)
              ? renderSkeletons(2)
              : null) as JSX.Element
          }>
          {loading ? renderSkeletons(2) : null}

          {list.map((shoutout, index) => (
            <ShoutoutCard
              key={`shoutout-card-${shoutout._id}-${index}`}
              type={filters.type}
              shoutout={shoutout}
              canPostToCommunity={(isViewerSelf && canPostInChannels) || false}
            />
          ))}

          {total > 0 && !loading && !hasMore ? (
            <Typography.Paragraph
              style={{
                width: '100%',
                textAlign: 'center',
                marginBlock: 32,
              }}>
              That’s it! No more shoutouts {filters.type}.
            </Typography.Paragraph>
          ) : null}
        </InfiniteScroll>

        {total === 0 && !loading && !hasMore ? (
          <NoShoutouts
            userName={userName}
            viewerType={viewerType}
            isViewerSelf={isViewerSelf}
            filters={filters}
          />
        ) : null}
      </div>
    </div>
  );
};

export default memo(UserShoutouts);
